var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { cloneElement } from 'react';
import { mergeProps, useFocusRing } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { logError, setTitleCase } from '../helpers';
import Counter from './counter';
import { MESSAGES } from './debug-messages';
import ButtonLoader from './loader';
import { buttonStyle, innerContentStyle, variantStyleMap } from './styles';
function ButtonInternal(_a, ref) {
    var { 'aria-label': ariaLabel, as, children, count, disableTitleCase = false, fullWidth = false, icon, iconLeft, iconRight, inverted = false, isLoading = false, size = 'medium', type, variant = 'regularOutline' } = _a, restProps = __rest(_a, ['aria-label', "as", "children", "count", "disableTitleCase", "fullWidth", "icon", "iconLeft", "iconRight", "inverted", "isLoading", "size", "type", "variant"]);
    const { i18n, ready } = useTranslation();
    const Element = as !== null && as !== void 0 ? as : 'button';
    const { focusProps, isFocusVisible } = useFocusRing();
    const typedRestProps = restProps;
    const themedStyle = variantStyleMap[variant];
    // Check whether the current language is English, so that we can enable title casing by default
    const isEnglishLanguage = ready && i18n.language === 'en-US';
    let buttonAriaLabel;
    if (typeof ariaLabel === 'string' || ariaLabel === undefined) {
        buttonAriaLabel = isLoading ? ariaLabel === null || ariaLabel === void 0 ? void 0 : ariaLabel.concat(' ', 'Loading') : ariaLabel;
    }
    else if (typeof ariaLabel === 'function') {
        buttonAriaLabel = ariaLabel(isLoading);
    }
    // Log console.error when an empty string is passed as incorrect syntax
    if (typeof children === 'string' && children.length === 0) {
        logError(MESSAGES.EMPTY_CHILDREN);
    }
    function renderIcon(originalIcon, key) {
        // Check if the Icon has a provided custom size prop already
        return originalIcon.props.size != null
            ? originalIcon
            : cloneElement(originalIcon, {
                key,
                size,
            });
    }
    return (_jsxs(Element, Object.assign({}, mergeProps(focusProps, typedRestProps), { "aria-label": buttonAriaLabel, css: buttonStyle({
            fullWidth,
            inverted,
            isFocusVisible,
            isIconOnly: !(icon == null),
            isLoading,
            size,
            themedStyle,
        }), ref: ref, type: type == null && Element === 'button' ? 'button' : type }, (isLoading && {
        disabled: true,
    }), { children: [icon != null ? (renderIcon(icon, 'button-icon-only')) : (_jsxs("span", Object.assign({ css: innerContentStyle({ size }) }, { children: [iconLeft != null && renderIcon(iconLeft, 'button-left-icon'), typeof children === 'string' &&
                        !disableTitleCase &&
                        isEnglishLanguage
                        ? setTitleCase(children)
                        : children, count !== undefined && (_jsx(Counter, Object.assign({}, {
                        count,
                        mode: themedStyle.badgeMode,
                        size,
                        variant: themedStyle.badgeVariant,
                    }))), iconRight != null && renderIcon(iconRight, 'button-right-icon')] }))), isLoading && (_jsx(ButtonLoader, Object.assign({}, {
                isIconOnly: !(icon == null),
                size,
                themedStyle,
            }), "button-loader"))] })));
}
export default ButtonInternal;
